import React, { useEffect, useRef, useState } from "react";
import * as prog from "core/src/progression/progression.js";
import { useProgress, claimDaily, type Challenge } from "../progress";
import { useUnopenedAchievements, openAchievements } from "../achievements-open";
import { eggBalanceNow, setEggBalance, refreshEggBalance, useEggBalance } from "../shop/useEggBalance";
import { runEggCelebration } from "../../pages/shop/purchase-celebration";
import { levelColor as achLevelColor } from "../../pages/play/match-end/UnlockCelebration";
import musicController from "../../audio/music-controller";
import GoldenEggIcon from "../GoldenEggIcon";
import { LevelRing } from "./LevelBadge";
import { useNextAchievement } from "./useNextAchievement";
import { ON_PORTAL } from "../portal-env";
import s from "./Daily.module.scss";

// The worm level, today's quests, the next achievement within reach and the
// achievements waiting to be opened, on the start page (shared/progress.ts):
//  column  the third column on a wide PC window: the level by its ring, the
//          next level that pays eggs, today's three (one done shrinks to a
//          line), the next achievement, and at the foot the gold card that
//          opens what waits
//  row     the row under the Royale on a narrower PC window
//  panel   the phone's panel under the player chip, the gold pill under it
//  sheet   the phone's panel that slides in (the panel opens it)
// The achievements to open open right here: the egg on the card rocks and
// cracks, and the eggs fly to your egg count (the card's own on PC and in the
// sheet, the Store button's on the phone's home screen). The eggs for a day
// with all three quests done are claimed the same way, under the quests.

const TIER = ["#55d16a", "#28c4d4", "#ffd23f"];

function untilMidnight(): string {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const mins = Math.max(1, Math.round((end.getTime() - now.getTime()) / 60000));
  return mins >= 60 ? `${Math.floor(mins / 60)} h ${mins % 60} min` : `${mins} min`;
}
// The next level that pays eggs.
const nextEggLevel = (level: number): { level: number; eggs: number } | null => prog.LEVEL_EGGS.find((l: { level: number }) => l.level > level) || null;
const pct = (a: number, b: number) => Math.max(0, Math.min(100, (a / Math.max(1, b)) * 100));

const Eggs = ({ n, size = 13 }: { n: number; size?: number }) => <span className={s.eggs}>+{n}<GoldenEggIcon size={size} /></span>;
const Bar = ({ v, of, color }: { v: number; of: number; color?: string }) => <span className={s.bar}><i style={{ width: `${pct(v, of)}%`, background: color }} /></span>;
const Check = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5l3.2 3L13 4.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

// "All three": the day's eggs, three pips filling as the quests get done.
function AllThree({ challenges }: { challenges: Challenge[] }) {
  const done = challenges.filter((c) => c.done).length;
  return (
    <span className={`${s.all} ${done === 3 ? s.allDone : ""}`}>
      <span className={s.pips} aria-hidden="true">{challenges.map((c) => <i key={c.id} className={c.done ? s.pipOn : ""} />)}</span>
      {done === 3 ? <span>All done for today</span> : <><span>All three</span><Eggs n={prog.DAILY_EGGS} /></>}
    </span>
  );
}

function QuestCard({ c }: { c: Challenge }) {
  if (c.done) {
    return (
      <div className={s.qDone} title={c.text}>
        <span className={s.qCheck}><Check size={11} /></span><b>{c.text}</b><em>+{c.xp} XP</em>
      </div>
    );
  }
  return (
    <div className={s.qCard} style={{ "--cc": TIER[c.tier] } as React.CSSProperties} title={c.detail ? `${c.text}, ${c.detail}` : c.text}>
      <span className={s.qTop}><b>{c.text}</b><em>+{c.xp} XP</em></span>
      <Bar v={c.have} of={c.target} color="var(--cc)" />
      <small>{c.have} / {c.target}</small>
    </div>
  );
}

// The next achievement within reach: its medal in the colour of the level it
// is working toward, how far, and what that level pays.
function NextAchievementCard() {
  const a = useNextAchievement();
  if (!a) return null;
  return (
    <div className={s.next}>
      <span className={s.label}><span>Next achievement</span><Eggs n={a.eggs} /></span>
      <span className={s.achRow}>
        <span className={s.medal} style={{ "--mc": achLevelColor(a.level) } as React.CSSProperties} aria-hidden="true">{a.icon}</span>
        <span className={s.achMain}>
          <b>{a.name}</b>
          <Bar v={a.value - a.from} of={a.to - a.from} color="#8f9cf5" />
          <small>{a.value.toLocaleString("en-US")} / {a.to.toLocaleString("en-US")}</small>
        </span>
      </span>
    </div>
  );
}

// The achievements waiting to be opened, as a gold card (a cell in the row, a
// pill on the phone). A click opens them all: the egg rocks three times,
// cracks, and the eggs fly to the count, each landing with its note; then the
// card fades away.
type OpenLook = "card" | "cell" | "pill";
function OpenRewards({ look }: { look: OpenLook }) {
  const unopened = useUnopenedAchievements();
  const balance = useEggBalance();
  const [phase, setPhase] = useState<"idle" | "crack" | "fly" | "done">("idle");
  const [shown, setShown] = useState<number | null>(null);
  const last = useRef(unopened);
  if (unopened.length) last.current = unopened;
  const eggRef = useRef<HTMLSpanElement | null>(null);
  const countRef = useRef<HTMLElement | null>(null);
  const [gone, setGone] = useState(!unopened.length);
  useEffect(() => { if (unopened.length) setGone(false); }, [unopened.length]);
  // (a game site's build has no accounts of ours: nothing waits to be opened)
  if (gone || ON_PORTAL) return null;
  const list = last.current;
  const eggs = list.reduce((n, u) => n + u.eggs, 0);
  const open = async () => {
    if (phase !== "idle") return;
    const before = eggBalanceNow();
    setShown(before);
    setPhase("crack");
    musicController.play("egg-wobble");
    const r = await openAchievements("all", true);
    await new Promise((res) => window.setTimeout(res, 640)); // the wobble's three knocks
    musicController.play("egg-crack");
    if ("error" in r) { setPhase("idle"); return; }
    if (!r.eggs || before == null) {
      r.commit();
      if (r.balance != null) setEggBalance(r.balance); else refreshEggBalance();
      setGone(true);
      setPhase("idle");
      return;
    }
    setPhase("fly");
    const target = look === "pill" ? document.querySelector<HTMLElement>("[data-store-eggs]") : countRef.current;
    await runEggCelebration({
      slot: null, total: r.eggs, notes: true, target,
      sources: eggRef.current ? [{ el: eggRef.current, count: r.eggs }] : [],
      onProgress: (landed) => { setEggBalance(before + landed); setShown(before + landed); },
    });
    if (r.balance != null) setEggBalance(r.balance);
    setPhase("done");
    musicController.play("egg-coin");
    window.setTimeout(() => { r.commit(); setGone(true); setPhase("idle"); }, 1600);
  };
  const busy = phase !== "idle";
  const label = list.length === 1 ? "1 achievement" : `${list.length} achievements`;
  const aria = `Open ${label}, +${eggs} Golden Eggs`;
  const egg = (
    <span ref={eggRef} className={`${s.oEgg} ${phase === "crack" ? s.rock : ""} ${phase === "fly" || phase === "done" ? s.cracked : ""}`} aria-hidden="true">
      <GoldenEggIcon size={look === "pill" ? 18 : look === "cell" ? 22 : 26} />
    </span>
  );
  if (look === "pill") {
    return (
      <button type="button" className={`${s.oPill} ${phase === "done" ? s.oFade : ""}`} onClick={() => void open()} disabled={busy} aria-label={aria}>
        {egg}<b>{list.length} to open</b><Eggs n={eggs} size={12} />
      </button>
    );
  }
  const counting = phase === "fly" || phase === "done";
  return (
    <button type="button" className={`${s.open} ${look === "cell" ? s.openCell : ""} ${phase === "done" ? s.oFade : ""}`} onClick={() => void open()} disabled={busy} aria-label={aria}>
      <span className={s.oShine} aria-hidden="true" />
      {egg}
      <span className={s.oText}>
        {counting
          ? <><small>Golden Eggs</small><b ref={(el) => { countRef.current = el; }} className={s.oCount}>{(shown ?? balance ?? 0).toLocaleString()}</b></>
          : <><b>{look === "cell" ? `${list.length} to open` : label}</b><Eggs n={eggs} /></>}
      </span>
      {look === "card" && <span className={s.oGo}>{counting ? <Eggs n={eggs} /> : "Open"}</span>}
    </button>
  );
}

// The eggs for the days with all three quests done (shared/progress.ts
// claimDaily), in the gold of the achievements' card: a card under the quests,
// a pill on the phone and in the narrow row. A click pays them and they fly to
// the count, each landing with its note.
function ClaimQuests({ look }: { look: "card" | "pill" }) {
  const p = useProgress();
  const balance = useEggBalance();
  const [phase, setPhase] = useState<"idle" | "fly" | "done">("idle");
  const [shown, setShown] = useState<number | null>(null);
  const [eggs, setEggs] = useState(p.toClaim);
  if (p.toClaim > 0 && phase === "idle" && eggs !== p.toClaim) setEggs(p.toClaim);
  const eggRef = useRef<HTMLSpanElement | null>(null);
  const countRef = useRef<HTMLElement | null>(null);
  if ((p.toClaim <= 0 && phase === "idle") || p.guest || ON_PORTAL) return null;
  const claim = async () => {
    if (phase !== "idle") return;
    const before = eggBalanceNow();
    setShown(before);
    setPhase("fly");
    musicController.play("egg-crack");
    const r = await claimDaily();
    if ("error" in r) { setPhase("idle"); return; }
    if (!r.eggs || before == null) {
      r.commit();
      if (r.balance != null) setEggBalance(r.balance); else refreshEggBalance();
      setPhase("idle");
      return;
    }
    const target = look === "pill" ? document.querySelector<HTMLElement>("[data-store-eggs]") : countRef.current;
    await runEggCelebration({
      slot: null, total: r.eggs, notes: true, target,
      sources: eggRef.current ? [{ el: eggRef.current, count: r.eggs }] : [],
      onProgress: (landed) => { setEggBalance(before + landed); setShown(before + landed); },
    });
    if (r.balance != null) setEggBalance(r.balance);
    setPhase("done");
    musicController.play("egg-coin");
    window.setTimeout(() => { r.commit(); setPhase("idle"); }, 1600);
  };
  const busy = phase !== "idle";
  const aria = `Claim ${eggs} Golden Eggs for today's quests`;
  const egg = (
    <span ref={eggRef} className={`${s.oEgg} ${busy ? s.cracked : ""}`} aria-hidden="true">
      <GoldenEggIcon size={look === "pill" ? 18 : 26} />
    </span>
  );
  if (look === "pill") {
    return (
      <button type="button" className={`${s.oPill} ${phase === "done" ? s.oFade : ""}`} onClick={() => void claim()} disabled={busy} aria-label={aria}>
        {egg}<b>Quests done</b><Eggs n={eggs} size={12} />
      </button>
    );
  }
  const counting = phase === "fly" || phase === "done";
  return (
    <button type="button" className={`${s.open} ${s.claim} ${phase === "done" ? s.oFade : ""}`} onClick={() => void claim()} disabled={busy} aria-label={aria}>
      <span className={s.oShine} aria-hidden="true" />
      {egg}
      <span className={s.oText}>
        {counting
          ? <><small>Golden Eggs</small><b ref={(el) => { countRef.current = el; }} className={s.oCount}>{(shown ?? balance ?? 0).toLocaleString()}</b></>
          : <><b>Quests done</b><Eggs n={eggs} /></>}
      </span>
      <span className={s.oGo}>{counting ? <Eggs n={eggs} /> : "Claim"}</span>
    </button>
  );
}

function SignIn({ onLogin }: { onLogin?: () => void }) {
  const p = useProgress();
  // (no sign-in of ours on a game site's build: shared/portal-env.ts)
  if (!(p.guest && p.guestRounds > 0 && onLogin) || ON_PORTAL) return null;
  return <button type="button" className={s.link} onClick={onLogin}>Sign in to keep your level</button>;
}

function Column({ onLogin }: { onLogin?: () => void }) {
  const p = useProgress();
  const nx = nextEggLevel(p.level);
  return (
    <div className={s.column}>
      <div className={s.head}>
        <LevelRing level={p.level} into={p.into} need={p.need} size={84} stroke={7} label={false} />
        <div className={s.headText}>
          <b>Level {p.level}</b>
          <span className={s.xp}><em>{p.into}</em> / {p.need} XP</span>
          {nx && <span className={s.nextLv}>Level {nx.level}<Eggs n={nx.eggs} /></span>}
        </div>
      </div>
      <div className={s.label}><span>Today's quests</span><span>New in {untilMidnight()}</span></div>
      <div className={s.quests}>{p.challenges.map((c) => <QuestCard key={c.id} c={c} />)}</div>
      <AllThree challenges={p.challenges} />
      <ClaimQuests look="card" />
      <SignIn onLogin={onLogin} />
      <NextAchievementCard />
      <div className={s.foot}><OpenRewards look="card" /></div>
    </div>
  );
}

function Row({ onLogin }: { onLogin?: () => void }) {
  const p = useProgress();
  const nx = nextEggLevel(p.level);
  const unopened = useUnopenedAchievements();
  const signIn = p.guest && p.guestRounds > 0 && onLogin && !ON_PORTAL;
  return (
    <div className={s.row}>
      <div className={s.rowHead}>
        <span className={s.label}>Today's quests</span>
        <span className={s.rowHeadR}>
          {signIn ? <SignIn onLogin={onLogin} /> : p.toClaim > 0 ? <ClaimQuests look="pill" /> : <AllThree challenges={p.challenges} />}
          <i />New in {untilMidnight()}
        </span>
      </div>
      <div className={`${s.rowGrid} ${unopened.length ? s.withOpen : ""}`}>
        <div className={s.rowLv}>
          <LevelRing level={p.level} into={p.into} need={p.need} size={46} stroke={4.5} label={false} />
          <span className={s.rowLvText}>
            <b>Level {p.level}</b>
            <span className={s.xp}><em>{p.into}</em> / {p.need} XP</span>
            {nx && <span className={s.nextLv}>Level {nx.level}<Eggs n={nx.eggs} size={11} /></span>}
          </span>
        </div>
        {p.challenges.map((c) => (
          <div key={c.id} className={`${s.rowQ} ${c.done ? s.rowQDone : ""}`} style={{ "--cc": TIER[c.tier] } as React.CSSProperties} title={c.text}>
            <span className={s.qTop}><b>{c.text}</b></span>
            <Bar v={c.done ? c.target : c.have} of={c.target} color={c.done ? "#55d16a" : "var(--cc)"} />
            <small><span>{c.done ? <><Check size={10} /> Done</> : `${c.have} / ${c.target}`}</span><span>+{c.xp} XP</span></small>
          </div>
        ))}
        <OpenRewards look="cell" />
      </div>
    </div>
  );
}

function PhoneQuests({ challenges }: { challenges: Challenge[] }) {
  return (
    <span className={s.pQs}>
      {challenges.map((c) => (
        <span key={c.id} className={`${s.pQ} ${c.done ? s.pQDone : ""}`} style={{ "--cc": TIER[c.tier] } as React.CSSProperties}>
          <span className={s.pQTop}><b>{c.text}</b><em>{c.done ? <Check size={10} /> : `${c.have}/${c.target}`}</em></span>
          <Bar v={c.done ? c.target : c.have} of={c.target} color={c.done ? "#55d16a" : "var(--cc)"} />
        </span>
      ))}
    </span>
  );
}

function Panel({ onOpen }: { onOpen?: () => void }) {
  const p = useProgress();
  const nx = nextEggLevel(p.level);
  return (
    <div className={s.panelWrap}>
      <button type="button" className={s.panel} onClick={onOpen} aria-label={`Level ${p.level}, today's quests`}>
        <span className={s.pLv}>
          <LevelRing level={p.level} into={p.into} need={p.need} size={40} stroke={4} label={false} />
          <span className={s.pLvText}>
            <b>Level {p.level}</b>
            <span className={s.xp}><em>{p.into}</em> / {p.need} XP{nx && <span className={s.pNext}><i />Level {nx.level}<Eggs n={nx.eggs} size={10} /></span>}</span>
          </span>
        </span>
        <PhoneQuests challenges={p.challenges} />
      </button>
      <ClaimQuests look="pill" />
      <OpenRewards look="pill" />
    </div>
  );
}

function Sheet({ onLogin }: { onLogin?: () => void }) {
  const p = useProgress();
  const nx = nextEggLevel(p.level);
  return (
    <div className={s.sheet}>
      <div className={s.sheetHead}>
        <LevelRing level={p.level} into={p.into} need={p.need} size={48} stroke={4.5} label={false} />
        <span className={s.headText}>
          <b>Level {p.level}</b>
          <span className={s.xp}><em>{p.into}</em> / {p.need} XP{nx && <span className={s.pNext}><i />Level {nx.level}<Eggs n={nx.eggs} size={11} /></span>}</span>
        </span>
      </div>
      <div className={s.label}><span>Today's quests</span><span>New in {untilMidnight()}</span></div>
      <PhoneQuests challenges={p.challenges} />
      <AllThree challenges={p.challenges} />
      <ClaimQuests look="card" />
      <SignIn onLogin={onLogin} />
      <NextAchievementCard />
      <OpenRewards look="card" />
    </div>
  );
}

export default function Daily({ column, row, panel, sheet, onLogin, onOpen }: { column?: boolean; row?: boolean; panel?: boolean; sheet?: boolean; onLogin?: () => void; onOpen?: () => void }) {
  // ("New in" counts down by the minute)
  const [, tick] = useState(0);
  useEffect(() => { const id = window.setInterval(() => tick((n) => n + 1), 60000); return () => window.clearInterval(id); }, []);
  if (column) return <Column onLogin={onLogin} />;
  if (row) return <Row onLogin={onLogin} />;
  if (panel) return <Panel onOpen={onOpen} />;
  if (sheet) return <Sheet onLogin={onLogin} />;
  return null;
}
